import { config } from "../config";
import type { Charity, ReferralSource } from "../types";

export interface UserDetails {
	first_name: string;
	last_name: string;
	country: string;
	referral_source: string;
	subscribe_to_updates: boolean;
}

export interface CharityAllocation {
	charity_id: number;
	amount_cents: number;
}

export interface DonationRequest {
	payment_method: "credit-card" | "bank-transfer";
	recurring: boolean;
	allocations: CharityAllocation[];
	user_details?: UserDetails;
}

interface CreditCardDonationResponse {
	checkout_url: string;
}

interface BankTransferDonationResponse {
	donation_id: number;
	message: string;
}

interface SendCodeResponse {
	success: boolean;
	message: string;
	cooldown_seconds?: number;
}

interface VerifyCodeResponse {
	success: boolean;
	message: string;
	user_id?: number;
}

async function postJSON<T>(
	url: string,
	body: unknown,
	errorMessage: string,
): Promise<T> {
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
		credentials: "include",
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.detail || errorMessage);
	}

	return await response.json();
}

/**
 * Internal helper for authenticated GET requests with optional 401 handling.
 */
async function internalGetJSON<T>(
	url: string,
	options?: { allowUnauthenticated?: boolean },
): Promise<T | null> {
	const response = await fetch(url, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
		credentials: "include",
	});

	if (options?.allowUnauthenticated && response.status === 401) {
		return null;
	}

	if (!response.ok) {
		throw new Error(
			`API request failed: ${response.status} ${response.statusText}`,
		);
	}

	return await response.json();
}

/**
 * Helper to make authenticated GET request
 */
async function getJSON<T>(url: string): Promise<T> {
	return (await internalGetJSON<T>(url)) as T;
}

/**
 * Helper to make authenticated GET request that treats 401 as "unauthenticated"
 * and returns null instead of throwing.
 */
async function getJSONAllowUnauthenticated<T>(url: string): Promise<T | null> {
	return await internalGetJSON<T>(url, { allowUnauthenticated: true });
}

export async function getPartnerCharities(): Promise<Charity[]> {
	return await getJSON<Charity[]>(`${config.apiBaseUrl}/partner_charities`);
}

export async function getReferralSources(): Promise<ReferralSource[]> {
	return await getJSON<ReferralSource[]>(
		`${config.apiBaseUrl}/referral_sources`,
	);
}

export async function submitCreditCardDonation(
	donationData: DonationRequest,
): Promise<CreditCardDonationResponse> {
	return await postJSON<CreditCardDonationResponse>(
		`${config.apiBaseUrl}/pledge_credit_card/`,
		donationData,
		"Failed to process credit card donation",
	);
}

export async function submitBankTransferDonation(
	donationData: DonationRequest,
): Promise<BankTransferDonationResponse> {
	return await postJSON<BankTransferDonationResponse>(
		`${config.apiBaseUrl}/pledge_bank_transfer/`,
		donationData,
		"Failed to process bank transfer donation",
	);
}

export async function createAccount(
	email: string,
	userDetails: UserDetails,
): Promise<SendCodeResponse> {
	return await postJSON<SendCodeResponse>(
		`${config.apiBaseUrl}/create_account`,
		{ email, user_details: userDetails },
		"Failed to create account",
	);
}

export async function signIn(email: string): Promise<SendCodeResponse> {
	return await postJSON<SendCodeResponse>(
		`${config.apiBaseUrl}/sign_in`,
		{ email },
		"Failed to sign in",
	);
}

export async function verifyCode(
	email: string,
	code: string,
): Promise<VerifyCodeResponse> {
	return await postJSON<VerifyCodeResponse>(
		`${config.apiBaseUrl}/verify_code`,
		{ email, code },
		"Failed to verify code",
	);
}

export interface SessionInfo {
	email: string;
	user_details: UserDetails | null;
	has_active_subscription: boolean;
}

export async function validateSession(): Promise<SessionInfo | null> {
	try {
		return await getJSON<SessionInfo>(`${config.apiBaseUrl}/validate_session`);
	} catch (error) {
		console.error("Session validation error:", error);
		return null;
	}
}

// Variant of session validation that fails silently without logging
export async function tryValidateSessionSilently(): Promise<SessionInfo | null> {
	return await getJSONAllowUnauthenticated<SessionInfo>(
		`${config.apiBaseUrl}/validate_session`,
	);
}

export async function logOut(): Promise<void> {
	await postJSON<{ success: boolean }>(
		`${config.apiBaseUrl}/logout`,
		{},
		"Failed to log out",
	);
}

export async function createPortalSession(): Promise<{ url: string }> {
	return await postJSON<{ url: string }>(
		`${config.apiBaseUrl}/create_portal_session`,
		{},
		"Failed to create billing portal session",
	);
}

export type PaymentStatus = "pending" | "paid" | "failed";
export type PaymentType = "one-time" | "recurring";
export type PaymentMethodType = "credit-card" | "bank-transfer";

export interface PaymentHistoryItem {
	invoice_id: number;
	donation_id: number;
	amount_cents: number;
	currency: string;
	status: PaymentStatus;
	payment_type: PaymentType;
	payment_method: PaymentMethodType;
	payment_date: string | null;
	receipt_available: boolean;
	hosted_invoice_url: string | null;
}

export interface PaymentHistoryResponse {
	payments: PaymentHistoryItem[];
}

export async function getPaymentHistory(): Promise<PaymentHistoryResponse> {
	return await getJSON<PaymentHistoryResponse>(
		`${config.apiBaseUrl}/payment_history`,
	);
}

// Default export for backward compatibility
const ApiService = {
	getPartnerCharities,
	getReferralSources,
	submitCreditCardDonation,
	submitBankTransferDonation,
	createAccount,
	signIn,
	verifyCode,
	validateSession,
	tryValidateSessionSilently,
	logOut,
	createPortalSession,
	getPaymentHistory,
};

export default ApiService;
